import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useCart } from "@/hooks/useCart"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery } from "@tanstack/react-query"
import * as z from "zod"
import { toastError } from "@/lib/toast"
import { OrderCreateDto, UserAddressDto, WarehouseDto } from "@/api/api-client"
import { apiClient } from "@/api/client"
import { userAddressSchema } from "@/lib/validationSchemas"
import { useLanguage } from "@/hooks/useLanguage"

export const checkoutSchema = userAddressSchema.extend({
    saveAddress: z.boolean().default(false).optional(),
    paymentMethod: z.enum(["now", "later"]).default("later"),
})

export type CheckoutFormValues = z.infer<typeof checkoutSchema>

export const useCheckout = () => {
    const { t } = useLanguage()
    const text = t.checkout
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [checkoutId, setCheckoutId] = useState<string | null>(null)
    const [selectedAddressId, setSelectedAddressId] = useState<number | "new" | null>(null)
    const { items, totalPrice, clearCart, setPickupWarehouse } = useCart()
    const navigate = useNavigate()
    const cartProductIds = Array.from(
        new Set(items.map((item) => item.id).filter((id): id is number => id !== undefined))
    )

    const form = useForm<CheckoutFormValues>({
        resolver: zodResolver(checkoutSchema),
        defaultValues: {
            receiverName: "",
            phone: "",
            address: {
                street: "",
                externalNumber: "",
                internalNumber: "",
                neighborhood: "",
                city: "",
                state: "",
                zipCode: "",
                country: "México",
            },
            saveAddress: false,
            paymentMethod: "later",
        },
    })

    const { data: savedAddresses = [], isLoading: isLoadingAddresses } = useQuery({
        queryKey: ["addresses"],
        queryFn: () => apiClient.getUserAddresses(),
    })

    const { data: warehouses = [] } = useQuery<WarehouseDto[]>({
        queryKey: ["warehouses"],
        queryFn: () => apiClient.getAllWarehouses(),
    })

    const { data: warehouseInventoryAvailability = [] } = useQuery({
        queryKey: ["warehouse-inventory-availability", cartProductIds],
        queryFn: () => apiClient.getWarehouseInventoryAvailability(cartProductIds),
        enabled: cartProductIds.length > 0,
    })

    const applyAddress = useCallback((addr: UserAddressDto) => {
        form.reset({
            ...form.getValues(),
            receiverName: addr.receiverName,
            phone: addr.phone,
            address: {
                street: addr.address.street,
                externalNumber: addr.address.externalNumber,
                internalNumber: addr.address.internalNumber || "",
                neighborhood: addr.address.neighborhood,
                city: addr.address.city,
                state: addr.address.state,
                zipCode: addr.address.zipCode,
                country: addr.address.country || "México",
            }
        })
    }, [form])

    useEffect(() => {
        if (savedAddresses.length > 0 && selectedAddressId === null) {
            const defaultAddr = savedAddresses.find((a) => a.isDefault) || savedAddresses[0]
            setSelectedAddressId(defaultAddr.id)
            applyAddress(defaultAddr)
        } else if (savedAddresses.length === 0 && !isLoadingAddresses && selectedAddressId === null) {
            setSelectedAddressId("new")
        }
    }, [savedAddresses, isLoadingAddresses, selectedAddressId, applyAddress])

    const handleAddressSelect = (id: number | "new") => {
        setSelectedAddressId(id)
        if (id === "new") {
            const currentValues = form.getValues()
            form.reset({
                ...currentValues,
                receiverName: "",
                phone: "",
                address: {
                    street: "",
                    externalNumber: "",
                    internalNumber: "",
                    neighborhood: "",
                    city: "",
                    state: "",
                    zipCode: "",
                    country: "México",
                },
                saveAddress: false,
            })
        } else {
            const addr = savedAddresses.find((a) => a.id === id)
            if (addr) applyAddress(addr)
        }
    }

    const onSubmit = async (values: CheckoutFormValues) => {
        if (items.length === 0) return
        setIsSubmitting(true)
        try {
            if (values.saveAddress && selectedAddressId === "new") {
                try {
                    await apiClient.createAddress({
                        receiverName: values.receiverName,
                        phone: values.phone,
                        address: values.address,
                        isDefault: savedAddresses.length === 0,
                    })
                } catch (addrError) {
                    console.error("Failed to save address:", addrError)
                    toastError("Order continuing, but address could not be saved.")
                }
            }

            const orderDto: OrderCreateDto = {
                receiverName: values.receiverName,
                phone: values.phone,
                street: values.address.street,
                externalNumber: values.address.externalNumber,
                internalNumber: values.address.internalNumber,
                neighborhood: values.address.neighborhood,
                city: values.address.city,
                state: values.address.state,
                zipCode: values.address.zipCode,
                items: items
                    .filter((item) => item.id !== undefined && item.id !== null)
                    .map((item) => ({
                        productId: item.id!,
                        quantity: item.quantity,
                        pickupWarehouseId: item.pickupWarehouseId,
                    })),
            }
            //TODO: Handle the scenario where a user doesn't go through the payment process after order creation. 
            // Should we cancel the order after some time or allow them to pay later from their orders page?
            // What if the user just clicks on "pay now" but then abandons the payment? We would have an order in "pending payment" status. How do we want to handle that?
            const order = await apiClient.createOrder(orderDto)

            // If user chose to pay later, skip Conekta and redirect to tracking
            if (values.paymentMethod === "later") {
                clearCart()
                navigate("/order-tracking")
                return
            }

            // Create Conekta Checkout Session
            if (order && order.id) {
                try {
                    const checkout = await apiClient.createCheckoutSession(order.id)
                    if (checkout && checkout.id) {
                        setCheckoutId(checkout.id)
                        return
                    } else {
                        toastError("Payment session could not be initialized.")
                    }
                } catch (checkoutError: any) {
                    console.error("Failed to create checkout session:", checkoutError)
                    const errorMsg = checkoutError.result || checkoutError.message || "Unknown error"
                    toastError(`Payment Error: ${errorMsg}. Order created. Redirecting...`)
                    clearCart()
                    navigate("/order-tracking")
                    return
                }
            } else {
                toastError("Order created but session failed. Please retry payment from your orders.")
            }

            clearCart()
            navigate("/order-tracking")
        } catch (error) {
            toastError(text.orderError)
        } finally {
            setIsSubmitting(false)
        }
    }

    return {
        isSubmitting,
        checkoutId,
        selectedAddressId,
        items,
        totalPrice,
        savedAddresses,
        isLoadingAddresses,
        warehouses,
        warehouseInventoryAvailability,
        setPickupWarehouse,
        form,
        text,
        navigate,
        clearCart,
        handleAddressSelect,
        onSubmit,
    }
}

